import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import ProfileMenu from "@/components/ProfileMenu";
import NotificationBell from "@/components/NotificationBell";
import { CoinIcon } from "@/components/CoinIcon";
import { useSidebarCollapsed } from "@/hooks/useSidebarCollapsed";
import {
  Briefcase, Users, ClipboardList, FileText, Compass, Sparkles,
  MessageSquare, LayoutGrid, ChevronsUpDown, PanelLeftClose, PanelLeftOpen, DoorOpen,
} from "lucide-react";

// ZenCRM-style left sidebar for the signed-in app on tablet + desktop (>= md).
// Collapsible to icons-only; collapsed state persists across sessions.
function navFor(user, adminLabel) {
  const core = { to: "/sales-types", label: "C.O.R.E.", Icon: Compass, testid: "sb-core" };
  const connie = { to: "/connie-connection", label: "Connie Connection", Icon: Sparkles, testid: "sb-connie" };
  const messages = { to: "/messages", label: "Messages", Icon: MessageSquare, testid: "sb-messages", badge: "unread" };
  if (user.role === "salesman") {
    return [
      { to: "/jobs", label: "Browse Jobs", Icon: Briefcase, testid: "sb-jobs" },
      { to: "/my-applications", label: "My Applications", Icon: ClipboardList, testid: "sb-applications" },
      { to: "/closing-process", label: "Closing Process", Icon: DoorOpen, testid: "sb-training" },
      core, connie, messages,
    ];
  }
  if (user.role === "business") {
    return [
      { to: "/talent", label: "Browse Talent", Icon: Users, testid: "sb-talent" },
      { to: "/applicants", label: "My Jobs", Icon: ClipboardList, testid: "sb-myjobs" },
      { to: "/forms", label: "Forms", Icon: FileText, testid: "sb-forms" },
      core, connie, messages,
    ];
  }
  return [
    core, connie, messages,
    { to: "/admin", label: adminLabel, Icon: LayoutGrid, testid: "sb-admin", special: true, badge: "support" },
  ];
}

export default function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [supportOpen, setSupportOpen] = useState(0);
  const [collapsed, setCollapsed] = useSidebarCollapsed();

  useEffect(() => {
    if (!user) return undefined;
    const loadUnread = () => api.get("/threads/unread-count").then((r) => setUnread(r.data?.count || 0)).catch(() => {});
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    const onMsg = () => loadUnread();
    window.addEventListener("messages-updated", onMsg);
    return () => { clearInterval(id); window.removeEventListener("messages-updated", onMsg); };
  }, [user, location.pathname]);

  useEffect(() => {
    if (user?.role === "owner" || user?.role === "admin") {
      const fetchCount = () => api.get("/admin/escalations/count").then((r) => setSupportOpen(r.data.open || 0)).catch(() => {});
      fetchCount();
      const t = setInterval(fetchCount, 30000);
      return () => clearInterval(t);
    }
    return undefined;
  }, [user]);

  if (!user) return null;
  const adminLabel = user.role === "owner" ? "Control Center" : user.role === "brand_ambassador" ? "My Dashboard" : "Admin";
  const items = navFor(user, adminLabel);
  const badgeVal = (t) => (t.badge === "unread" ? unread : t.badge === "support" ? supportOpen : 0);

  return (
    <aside data-testid="app-sidebar"
      className={`hidden md:flex fixed inset-y-0 left-0 z-40 flex-col border-r border-white/10 bg-slate-900/95 backdrop-blur-xl transition-[width] duration-200 ${collapsed ? "w-16" : "w-60"}`}>
      <div className={`h-20 flex items-center shrink-0 ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
        {!collapsed && <Logo />}
        <button data-testid="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} title={collapsed ? "Expand" : "Collapse"}
          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
          {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
        </button>
      </div>

      {user.role === "business" && (
        <div className={collapsed ? "px-2 pb-2 flex justify-center" : "px-4 pb-3"}>
          <NavLink to="/pricing" data-testid="sb-credits" title={`${user.credits ?? 0} Core Credits`}
            className={`flex items-center gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/20 transition-colors ${collapsed ? "p-2" : "px-3 py-2"}`}>
            <CoinIcon className="w-5 h-5 shrink-0" />
            {!collapsed && <><span>{user.credits ?? 0}</span> <span className="text-emerald-400/70 font-normal">Core Credits</span></>}
          </NavLink>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1" data-testid="sidebar-nav">
        {items.map((t) => {
          const n = badgeVal(t);
          return (
            <NavLink key={t.testid} to={t.to} data-testid={t.testid} title={collapsed ? t.label : undefined}
              className={({ isActive }) => {
                const base = `group relative flex items-center rounded-xl text-[15px] font-medium transition-colors outline-none ${collapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2.5"}`;
                if (t.special) {
                  return `${base} ${isActive ? "bg-slate-950 text-white ring-1 ring-violet-400/40" : "bg-slate-950/70 text-slate-200 hover:bg-slate-950 hover:text-white"}`;
                }
                return `${base} ${isActive ? "bg-violet-500/15 text-violet-200 ring-1 ring-violet-400/30" : "text-slate-300 hover:text-white hover:bg-white/5"}`;
              }}>
              {({ isActive }) => (
                <>
                  <t.Icon className={`w-5 h-5 shrink-0 ${t.special ? "text-violet-300" : ""}`} strokeWidth={isActive ? 2.4 : 2} />
                  {!collapsed && <span className="truncate">{t.label}</span>}
                  {n > 0 && (collapsed ? (
                    <span data-testid={`${t.testid}-badge`} className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-slate-900" />
                  ) : (
                    <span data-testid={`${t.testid}-badge`} className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">
                      {n > 9 ? "9+" : n}
                    </span>
                  ))}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className={`shrink-0 border-t border-white/10 p-3 flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}>
        <ProfileMenu side="top" align="start" trigger={
          collapsed ? (
            <button data-testid="sidebar-profile" aria-label="Profile" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors outline-none">
              <img src="/profile-icon.png" alt="" className="w-7 h-7 object-contain" />
            </button>
          ) : (
            <button data-testid="sidebar-profile" className="flex-1 min-w-0 flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-white/5 transition-colors outline-none">
              <span className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <img src="/profile-icon.png" alt="" className="w-7 h-7 object-contain" />
              </span>
              <span className="min-w-0 text-left">
                <span className="block text-sm font-semibold text-white truncate">{user.name || "My account"}</span>
                <span className="block text-xs text-slate-400 truncate capitalize">{(user.role || "").replace("_", " ")}</span>
              </span>
              <ChevronsUpDown className="w-4 h-4 text-slate-500 shrink-0" />
            </button>
          )
        } />
        {!collapsed && <NotificationBell />}
      </div>
    </aside>
  );
}
